import React, { CSSProperties, useCallback, useContext, useEffect, useReducer } from 'react';
import { NavBarView } from './Navbar.view';
import { AnalyticsContext, SocialProfilesAnalytics } from '../../AnalyticsProvider';
import { useForm, useWatch } from 'react-hook-form';
import { AnalyticsVisualizationType } from '../AnalyticsDashboard.controller';
import { showToast } from '../../../../helpers/showToast';
import { ErrorMessageSeverity } from 'constants/form';
import { ValidationMessages } from 'constants/validation-messages';
import { differenceInDays, isBefore, isSameDay } from 'date-fns';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { PlanAnalyticsType } from 'models/graphql';
import { AppContext } from 'providers/AppProvider';
import { Link } from 'react-router-dom';
import { AnalyticsNavbarReducer, AnalyticsNavbarState } from './Navbar.reducer';
import {
  SocialProfileFields_GenericSocialProfile_Fragment,
  SocialProfileFields_InstagramSocialProfile_Fragment,
  InstagramApiType,
  SocialProfileFieldsFragment
} from '../../../../models/graphql';

type NavbarControllerProps = {
  shouldShowSidebar: boolean;
};

const checkTypeInstaProfile = (
  allSocialProfiles: SocialProfileFieldsFragment[],
  instaProfiles: SocialProfileFieldsFragment[],
  selectedProfileIds: string[]
) => {
  const hasInstaProfiles = selectedProfileIds.some(id => instaProfiles.find(instaProfile => instaProfile.id === id));
  if (instaProfiles.length === 0 || !hasInstaProfiles) {
    return;
  }
  const instaAPI = allSocialProfiles.filter(
    profile => profile.__typename === 'InstagramSocialProfile' && profile.apiType === InstagramApiType.INSTAGRAM_GRAPH
  );
  const facebookAPI = allSocialProfiles.filter(
    profile => profile.__typename === 'InstagramSocialProfile' && profile.apiType === InstagramApiType.FACEBOOK_GRAPH
  );
  const hasInstaAPI =
    instaAPI.length > 0 && instaAPI.some(profile => profile.id === selectedProfileIds.find(id => id === profile.id));
  const hasFacebokAPI =
    instaAPI.length > 0 && facebookAPI.some(profile => profile.id === selectedProfileIds.find(id => id === profile.id));
  if (hasInstaAPI && hasFacebokAPI) {
    showToast({
      severity: 'warn',
      summary: 'Partial Stats',
      sticky: false,
      detail: (
        <>
          Some profiles in this report only include Likes and Comments metrics, and only for Instagram posts published
          directly through SmarterQueue. To view more metrics,{' '}
          <Link to={'/settings/profiles'}>re-connect your profile via a Facebook Page.</Link>{' '}
        </>
      )
    });
  } else if (hasInstaAPI) {
    showToast({
      severity: 'warn',
      summary: 'Partial Stats',
      sticky: false,
      detail: (
        <>
          This report only includes Likes and Comments metrics, and only for Instagram posts published directly through
          SmarterQueue. To view more metrics,{' '}
          <Link to={'/settings/profiles'}>re-connect your profile via a Facebook Page.</Link>{' '}
        </>
      )
    });
  } else if (hasFacebokAPI) {
    showToast({
      severity: 'warn',
      summary: 'Partial Stats',
      sticky: false,
      detail: <>This report only includes Instagram posts published directly through SmarterQueue.</>
    });
  }
};

const isGenericProfile = (
  profile: SocialProfileFieldsFragment
): profile is SocialProfileFields_GenericSocialProfile_Fragment => {
  return (profile as SocialProfileFields_GenericSocialProfile_Fragment).network === 'GENERIC_PROFILE';
};

const notDirectPublishedProfile = (
  profile: SocialProfileFieldsFragment
): profile is SocialProfileFields_GenericSocialProfile_Fragment => {
  if (isGenericProfile(profile)) {
    return true;
  }

  return (
    profile.__typename === 'InstagramSocialProfile' &&
    !(profile as SocialProfileFields_InstagramSocialProfile_Fragment).directPublishEnabled
  );
};

const getProfilesToWarn = (
  socialProfiles: SocialProfileFieldsFragment[]
): SocialProfileFields_GenericSocialProfile_Fragment[] => {
  const notDirect = socialProfiles.filter(notDirectPublishedProfile);

  return [...notDirect];
};

// Note: We should consider making this its own small component and moving styles to a class
const getWarningMessage = (profiles: Partial<SocialProfileFields_GenericSocialProfile_Fragment>[]) => {
  const list = profiles.map(p => {
    // temp removed until this can be better considered
    const platform = ''; // p.platformName ? `(${p.platformName})` : '';
    const itemStyle: CSSProperties = { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' };

    return (
      <li key={p.id}>
        <div style={itemStyle}>
          {p.displayName} {platform}
        </div>
      </li>
    );
  });

  return <ul>{list}</ul>;
};

const warnIfNotDirectPublishProfileIsPresent = (socialProfiles: SocialProfileFieldsFragment[]) => {
  const genericProfiles = getProfilesToWarn(socialProfiles);
  const detail = getWarningMessage(genericProfiles);

  if (genericProfiles.length > 0) {
    showToast({
      severity: 'warn',
      summary: 'Limited stats are available for these profiles',
      detail,
      sticky: false
    });
  }
};

const getMatchedSelectedProfiles = (socialProfilesPrimitive: string, profileIds: string[]) => {
  return profileIds.map((socialProfileId: string) => {
    const parsedSocialProfiles: SocialProfilesAnalytics[] = JSON.parse(socialProfilesPrimitive);
    const matchedProfile = parsedSocialProfiles.find(profile => profile.id === socialProfileId);

    if (!matchedProfile) {
      throw Error;
    }
    return {
      __typename: matchedProfile.__typename,
      profileId: matchedProfile.id
    };
  });
};

export const NavbarController = ({ shouldShowSidebar }: NavbarControllerProps) => {
  const { view, setView, setStartDate, setEndDate, setSelectedProfileIds, socialProfiles, startDate, endDate } =
    useContext(AnalyticsContext);
  const { setLimitReachedModalState } = useContext(AppContext);
  const initialState: AnalyticsNavbarState = {
    appliedStartDateOverview: startDate,
    appliedEndDateOverview: endDate,
    appliedStartDateByProfileView: startDate,
    appliedEndDateByProfileView: endDate,
    appliedProfileIdsOverview: [],
    appliedProfileId: '',
    selectedProfilesOverview: [],
    selectedProfileId: undefined,
    profileErrorStateOverview: null,
    profileErrorState: null
  };
  const [state, dispatch] = useReducer(AnalyticsNavbarReducer, initialState);
  const {
    appliedProfileIdsOverview,
    appliedStartDateByProfileView,
    appliedEndDateByProfileView,
    appliedProfileId,
    appliedStartDateOverview,
    appliedEndDateOverview
  } = state;
  const formInstance = useForm();
  const { control, getValues, setValue, unregister, setError } = formInstance;
  const startDateFormValue = useWatch({ control, name: 'startRange', defaultValue: startDate });
  const endDateFormValue = useWatch({ control, name: 'endRange', defaultValue: endDate });
  const valueSocialProfilesMultipleSelector: (string | boolean)[] = useWatch({
    control,
    name: 'selectedProfilesOverview'
  });
  const { analyticsType, isTrial } = useOrganizationPlan();
  const uniqueProfileValue = useWatch({ control, name: 'uniqueProfile' });
  const formValues = useWatch({ control });
  const valueSocialProfilesMultipleSelectorPrimitive =
    valueSocialProfilesMultipleSelector && valueSocialProfilesMultipleSelector.length
      ? JSON.stringify(valueSocialProfilesMultipleSelector.filter(item => typeof item === 'string').sort())
      : '';
  const socialProfilesPrimitive = JSON.stringify(socialProfiles);
  const isViewOverview = view === AnalyticsVisualizationType.overview;

  const validateQtyProfilesCallback = useCallback(() => {
    if (!valueSocialProfilesMultipleSelectorPrimitive) {
      return;
    }
    const parsedSocialProfiles: string[] = JSON.parse(valueSocialProfilesMultipleSelectorPrimitive);
    if (parsedSocialProfiles.length >= 13) {
      dispatch({
        type: 'SET_PROFILE_ERROR_OVERVIEW',
        payload: {
          errorSeverity: ErrorMessageSeverity.error,
          message: 'You can analyze only up to 12 profiles at once'
        }
      });
      return;
    }
    dispatch({ type: 'SET_PROFILE_ERROR_OVERVIEW', payload: null });
  }, [valueSocialProfilesMultipleSelectorPrimitive]);
  const overOneMonthNotAllowed =
    (analyticsType !== PlanAnalyticsType.ADVANCED_ANALYTICS || isTrial) &&
    Math.abs(differenceInDays(startDateFormValue, endDateFormValue)) >= 32;

  const viewFormValue = useWatch({ control, name: 'view', defaultValue: AnalyticsVisualizationType.overview });

  useEffect(() => {
    setView(parseInt(viewFormValue));
  }, [viewFormValue, setView]);

  const isValidDateRange =
    isBefore(startDateFormValue, endDateFormValue) || isSameDay(startDateFormValue, endDateFormValue);

  const applyFilters = useCallback(() => {
    const instaProfiles = socialProfiles.filter(profile => profile.__typename === 'InstagramSocialProfile');
    const socialProfileValue = getValues('uniqueProfile');
    const profilesIds =
      isViewOverview && valueSocialProfilesMultipleSelector
        ? valueSocialProfilesMultipleSelector?.filter(profileId => typeof profileId === 'string')
        : socialProfileValue && [socialProfileValue];

    const isProfilesValid = profilesIds?.length > 0;
    const shouldApplyFilters = isValidDateRange && isProfilesValid;

    if (shouldApplyFilters) {
      if (overOneMonthNotAllowed) {
        setLimitReachedModalState({
          isOpen: true,
          type: 'analytics'
        });
        return;
      }
      //TODO: Refactor to useReducer
      checkTypeInstaProfile(socialProfiles, instaProfiles, profilesIds);
      setStartDate(startDateFormValue);
      setEndDate(endDateFormValue);
      if (isViewOverview) {
        dispatch({
          type: 'APPLY_FILTERS_OVERVIEW',
          payload: {
            profilesIds: profilesIds,
            startDate: startDateFormValue,
            endDate: endDateFormValue
          }
        });
      } else {
        dispatch({
          type: 'APPLY_FILTERS_PROFILE',
          payload: {
            profileId: profilesIds[0],
            startDate: startDateFormValue,
            endDate: endDateFormValue
          }
        });
      }

      warnIfNotDirectPublishProfileIsPresent(socialProfiles.filter(p => profilesIds.includes(p.id)));
    } else {
      if (!isProfilesValid) {
        isViewOverview
          ? dispatch({
              type: 'SET_PROFILE_ERROR_OVERVIEW',
              payload: {
                errorSeverity: ErrorMessageSeverity.error,
                message: ValidationMessages.analytics.profiles.required.overview
              }
            })
          : dispatch({
              type: 'SET_PROFILE_ERROR',
              payload: {
                errorSeverity: ErrorMessageSeverity.error,
                message: ValidationMessages.analytics.profiles.required.perProfile
              }
            });
      }
      !isValidDateRange && setError('startRange', { message: 'Set a past date' });
    }
  }, [
    endDateFormValue,
    getValues,
    isValidDateRange,
    isViewOverview,
    setEndDate,
    setError,
    setStartDate,
    socialProfiles,
    startDateFormValue,
    valueSocialProfilesMultipleSelector,
    setLimitReachedModalState,
    overOneMonthNotAllowed
  ]);

  const formNamesPrimitive = JSON.stringify(Object.keys(formValues));
  const getFormNamesFromSelectProfilesComponent = useCallback(
    () => JSON.parse(formNamesPrimitive).filter((fieldName: string) => fieldName.includes('select')),
    [formNamesPrimitive]
  );
  useEffect(() => {
    if (isValidDateRange) {
      formInstance.clearErrors('startRange');
    }
  }, [startDateFormValue, endDateFormValue, isValidDateRange, formInstance]);
  // Change the profiles selector to the applied filters

  useEffect(() => {
    if (view === AnalyticsVisualizationType.profile) {
      // We need to reset all forms that begin with select, there are some internal form names for the select all checkboxes
      const formNamesToReset = getFormNamesFromSelectProfilesComponent();
      formNamesToReset.forEach((name: string) => {
        unregister(name);
      });
      setValue('selectedProfilesOverview', appliedProfileIdsOverview);
      setStartDate(appliedStartDateByProfileView);
      setEndDate(appliedEndDateByProfileView);
      setSelectedProfileIds(appliedProfileId ? [appliedProfileId] : []);
    } else {
      setStartDate(appliedStartDateOverview);
      setEndDate(appliedEndDateOverview);
      setValue('uniqueProfile', appliedProfileId);
      setSelectedProfileIds(appliedProfileIdsOverview);
    }
  }, [
    view,
    appliedProfileIdsOverview,
    unregister,
    getFormNamesFromSelectProfilesComponent,
    appliedProfileId,
    setValue,
    setSelectedProfileIds,
    setStartDate,
    setEndDate,
    appliedStartDateByProfileView,
    appliedEndDateByProfileView,
    appliedStartDateOverview,
    appliedEndDateOverview
  ]);

  useEffect(() => {
    const profileIds = valueSocialProfilesMultipleSelectorPrimitive
      ? JSON.parse(valueSocialProfilesMultipleSelectorPrimitive) || []
      : [];

    if (profileIds.length >= 13) {
      showToast({
        severity: 'error',
        summary: 'Selection limit reached',
        detail: 'You can only view reports for up to 12 profiles at a time. Please select fewer profiles.',
        sticky: false
      });
    }

    if (profileIds.length > 0) {
      dispatch({ type: 'SET_PROFILE_ERROR_OVERVIEW', payload: null });
      const profiles = getMatchedSelectedProfiles(socialProfilesPrimitive, profileIds);
      dispatch({
        type: 'SET_SELECTED_PROFILES_OVERVIEW',
        payload: profiles
      });
    } else {
      dispatch({
        type: 'SET_SELECTED_PROFILES_OVERVIEW',
        payload: []
      });
    }
  }, [valueSocialProfilesMultipleSelectorPrimitive, socialProfilesPrimitive]);

  useEffect(() => {
    if (uniqueProfileValue) {
      dispatch({ type: 'SET_UNIQUE_PROFILE', payload: uniqueProfileValue });
    }
  }, [uniqueProfileValue]);

  return (
    <NavBarView
      formInstance={formInstance}
      shouldShowSidebar={shouldShowSidebar}
      applyFilterFunction={applyFilters}
      isViewOverview={isViewOverview}
      navbarState={state}
      overOneMonthNotAllowed={overOneMonthNotAllowed}
      validateProfile={validateQtyProfilesCallback}></NavBarView>
  );
};
